import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { LangProvider } from '@/utils/i18n';
import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import AdminDashboard from '@/pages/AdminDashboard';
import PricingPage from '@/pages/PricingPage';
import SuccessPage from '@/pages/SuccessPage';
import ProfilePage from '@/pages/ProfilePage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import FeedPage from '@/pages/FeedPage';
import LeaderboardPage from '@/pages/LeaderboardPage';
import ChallengePage from '@/pages/ChallengePage';
import SupportPage from '@/pages/SupportPage';
import FAQPage from '@/pages/FAQPage';
import ImpressumPage from '@/pages/ImpressumPage';
import DatenschutzPage from '@/pages/DatenschutzPage';
import AGBPage from '@/pages/AGBPage';
import TipSharingPage from '@/pages/TipSharingPage';
import MessagesPage from '@/pages/MessagesPage';
import SimulatorPage from '@/pages/SimulatorPage';
import AILearningPage from '@/pages/AILearningPage';
import LiveChatPage from '@/pages/LiveChatPage';
import SocialPage from '@/pages/SocialPage';
import AIChallengesPage from '@/pages/AIChallengesPage';
import MultiSportPage from '@/pages/MultiSportPage';
import DailyTournamentPage from '@/pages/DailyTournamentPage';
import FriendsPage from '@/pages/FriendsPage';
import PremiumPage from '@/pages/PremiumPage';
import WeeklyJackpotPage from '@/pages/WeeklyJackpotPage';
import MatchSearchPage from '@/pages/MatchSearchPage';
import { isAuthenticated } from '@/utils/auth';
import AIChatAssistant from '@/components/AIChatAssistant';
import CookieBanner from '@/components/CookieBanner';
import OfflineIndicator from '@/components/OfflineIndicator';
import PushPermissionPrompt from '@/components/PushPermissionPrompt';

const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/auth" />;
};

function App() {
  const [authed, setAuthed] = React.useState(isAuthenticated());

  // Re-check auth on localStorage changes (e.g. after login/logout)
  React.useEffect(() => {
    const check = () => setAuthed(isAuthenticated());
    window.addEventListener('storage', check);
    // Also check on route changes
    const interval = setInterval(check, 1000);
    return () => {
      window.removeEventListener('storage', check);
      clearInterval(interval);
    };
  }, []);

  return (
    <LangProvider>
      <div className="App">
        <OfflineIndicator />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/success" element={<SuccessPage />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/feed"
              element={
                <ProtectedRoute>
                  <FeedPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <LeaderboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/challenges"
              element={
                <ProtectedRoute>
                  <ChallengePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={<AdminDashboard />}
            />
            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tip-sharing"
              element={
                <ProtectedRoute>
                  <TipSharingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/simulator"
              element={
                <ProtectedRoute>
                  <SimulatorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-learning"
              element={
                <ProtectedRoute>
                  <AILearningPage />
                </ProtectedRoute>
              }
            />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
            <Route path="/datenschutz" element={<DatenschutzPage />} />
            <Route path="/agb" element={<AGBPage />} />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <LiveChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/social"
              element={
                <ProtectedRoute>
                  <SocialPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/ai-challenges"
              element={
                <ProtectedRoute>
                  <AIChallengesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/multi-sport"
              element={
                <ProtectedRoute>
                  <MultiSportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/daily-tournament"
              element={
                <ProtectedRoute>
                  <DailyTournamentPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/friends"
              element={
                <ProtectedRoute>
                  <FriendsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/premium"
              element={
                <ProtectedRoute>
                  <PremiumPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/weekly-jackpot"
              element={
                <ProtectedRoute>
                  <WeeklyJackpotPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/match-search"
              element={
                <ProtectedRoute>
                  <MatchSearchPage />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          {authed && <AIChatAssistant />}
          {authed && <PushPermissionPrompt />}
          <CookieBanner />
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#1E1E1E',
              color: '#fff',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            },
          }}
        />
      </div>
    </LangProvider>
  );
}

export default App;
